import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import styled from 'styled-components/macro'

import { Button } from '@loadsmart/loadsmart-ui'
import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { acceptIntegrationRequest, rejectIntegrationRequest } from 'legacy/services/carrier'
import { analytics } from 'legacy/utils/analytics'

const IntegrationRequest = styled.div`
  background-color: ${props =>
    props.status === 'ACCEPTED'
      ? getToken('color-success-light')
      : getToken('color-warning-light')};
  border: 1px solid
    ${props =>
      props.status === 'ACCEPTED' ? getToken('color-success') : getToken('color-warning')};
  border-radius: ${getToken('border-radius-m')};
  display: flex;
  flex-direction: column;
  font-family: ${getToken('font-family-default')};
  font-size: 16px;
  padding: 12px 15px;

  & > *:not(:last-child) {
    margin-bottom: 10px;
  }
`

const Banner = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const Question = styled.div`
  align-items: center;
  display: flex;
`

const Actions = styled.div`
  display: flex;
  justify-content: center;

  & > *:not(:last-child) {
    margin-right: 10px;
  }
`

const Error = styled.div`
  color: ${getToken('color-danger-dark')};
  font-size: 14px;
  font-weight: 400;
  text-align: right;
`

const Message = styled.div`
  align-items: center;
  display: flex;
  height: 37px;
`

export function ThirdPartyRequest({ partnerName, id }) {
  const [error, setError] = useState(null)
  const [message, setMessage] = useState(null)
  const [status, setStatus] = useState('PENDING')

  useEffect(() => {
    analytics.track('Integration Request / View')
  }, [])

  async function acceptRequest() {
    try {
      await acceptIntegrationRequest(id)
      setStatus('ACCEPTED')
      setError(null)
      setMessage(`${partnerName} can now manage loads, capacity and drivers on your behalf!`)
      analytics.track('Integration Request / Accepted')
    } catch {
      setStatus('ACCEPT_ERROR')
      setError('Error while accepting the integration request. Please try again.')
      analytics.track('Integration Request / Accept Error')
    }
  }

  async function rejectRequest() {
    try {
      await rejectIntegrationRequest(id)
      setStatus('REJECTED')
      setError(null)
      setMessage(
        `You rejected the request for ${partnerName} to manage loads, capacity and drivers on your behalf.`
      )
      analytics.track('Integration Request / Rejected')
    } catch {
      setStatus('REJECT_ERROR')
      setError('Error while rejecting the integration request. Please try again.')
      analytics.track('Integration Request / Reject Error')
    }
  }

  let shouldDisplayQuestion = status === 'PENDING' || status.includes('ERROR')

  return (
    <IntegrationRequest status={status}>
      {shouldDisplayQuestion && (
        <Banner>
          <Question>
            {partnerName} wants to manage loads, capacity and drivers on your behalf. Do you accept?
          </Question>
          <Actions>
            <Button onClick={acceptRequest} variant="primary">
              Accept
            </Button>
            <Button onClick={rejectRequest} variant="secondary">
              Reject
            </Button>
          </Actions>
        </Banner>
      )}
      {error && <Error>{error}</Error>}
      {message && <Message>{message}</Message>}
    </IntegrationRequest>
  )
}

ThirdPartyRequest.propTypes = {
  id: PropTypes.string,
  partnerName: PropTypes.string,
}

ThirdPartyRequest.defaultProps = {
  id: '',
  partnerName: '',
}
